/* Size finder — maatadvies uit geslacht + leeftijd + lengte + gewicht.
   Schat borst/taille/heup via regressie (Heinz body-dataset, n=507; taille
   gecorrigeerd met NHANES/Bozeman 2012 bij BMI >= 28), matcht tegen de
   maattabel via aaneengesloten bakken (middelpunt-interpolatie tussen maten).
   Alles client-side; profiel in localStorage, nooit naar een server. */

if (!customElements.get('size-finder')) {
  const STORE_KEY = 'dt:size-finder';

  const storage = {
    get(key) {
      try { return localStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { localStorage.setItem(key, value); } catch (e) { /* private mode */ }
    },
    remove(key) {
      try { localStorage.removeItem(key); } catch (e) { /* private mode */ }
    }
  };

  /* ---- Lichaamsmodel ---- */

  // [intercept, gewicht(kg), lengte(cm), leeftijd(jr)] per omvang, per geslacht
  const COEFFS = {
    male: {
      chest: [92.770, 0.6232, -0.2383, 0.0594],
      waist: [74.360, 0.7521, -0.3223, 0.2744],
      hip: [78.251, 0.5697, -0.1443, 0.0204]
    },
    female: {
      chest: [80.029, 0.5889, -0.1847, 0.0275],
      waist: [73.318, 0.7583, -0.3138, 0.0785],
      hip: [69.524, 0.6707, -0.1016, 0.0775]
    }
  };

  // Gewicht per dimensie bij het combineren van tabelkolommen
  const WEIGHTS = { chest: 0.55, waist: 0.3, hip: 0.15, inseam: 0.08, height: 0.2 };
  const GIRTHS = ['chest', 'waist', 'hip'];

  const lin = (c, kg, cm, age) => c[0] + c[1] * kg + c[2] * cm + c[3] * age;
  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  function estimateBody(gender, age, heightCm, weightKg) {
    const genders = gender === 'other' ? ['male', 'female'] : [gender];
    const bmi = weightKg / ((heightCm / 100) ** 2);
    const est = {};
    ['chest', 'hip'].forEach((key) => {
      est[key] = genders.reduce((sum, g) => sum + lin(COEFFS[g][key], weightKg, heightCm, age), 0) / genders.length;
    });
    // Taille geijkt op gemeten NL-data (Lifelines n=74k: M 94.9 / V 86.6;
    // validatie werkende populatie n=1298): de kale OLS zit bij vrouwen ~5 cm
    // te laag, dus vrouwen krijgen de NHANES-blend altijd; bij mannen klopt de
    // OLS rond het NL-gemiddelde en blendt hij alleen vanaf BMI 28.
    est.waist = genders.reduce((sum, g) => {
      const ols = lin(COEFFS[g].waist, weightKg, heightCm, age);
      const nhanes = g === 'male'
        ? 22.61306 + 2.520738 * bmi + 0.1583812 * age
        : 28.81919 + 2.218007 * bmi + (age >= 35 ? -3.688953 + 0.125975 * age : 0);
      const blended = (g === 'female' || bmi >= 28) ? (ols + nhanes) / 2 : ols;
      return sum + blended;
    }, 0) / genders.length;
    est.inseam = 0.45 * heightCm;
    est.height = heightCm;
    Object.keys(est).forEach((k) => { est[k] = Math.round(est[k] * 2) / 2; });
    return est;
  }

  /* ---- Maattabel-matching ---- */

  function cellRange(value) {
    if (value == null) return null;
    if (Array.isArray(value)) {
      const min = parseFloat(value[0]);
      const max = parseFloat(value[value.length - 1]);
      if (Number.isNaN(min) || Number.isNaN(max)) return null;
      return [Math.min(min, max), Math.max(min, max)];
    }
    const num = parseFloat(value);
    return Number.isNaN(num) ? null : [num, num];
  }

  // Aaneengesloten bakken per kolom: grens = middelpunt tussen twee maten,
  // zodat er geen dood gebied valt tussen bv. "92-96" en "98-102".
  function buildBins(rows, key) {
    const entries = [];
    rows.forEach((row, i) => {
      const range = cellRange(row[key]);
      if (range) entries.push({ row: i, min: range[0], max: range[1] });
    });
    if (entries.length < 2) return null;
    for (let i = 1; i < entries.length; i += 1) {
      if (entries[i].min < entries[i - 1].min) return null; // niet-oplopend: onbruikbaar
    }
    // (Vrijwel) constante kolom — bv. binnenbeen "79" bij elke maat (Deerhunter,
    // Chevalier dames) — onderscheidt niets en zou elke afwijkende schatting
    // "buiten de tabel" verklaren: overslaan als signaal.
    if (entries[entries.length - 1].max - entries[0].min < 2) return null;
    return entries.map((e, i) => ({
      row: e.row,
      min: i === 0 ? e.min : (entries[i - 1].max + e.min) / 2,
      max: i === entries.length - 1 ? e.max : (e.max + entries[i + 1].min) / 2
    }));
  }

  // Continue maat-coordinaat: maat k beslaat [k, k+1). Buiten bereik telt in
  // gemiddelde bakbreedtes door (afgekapt), met een out-flag als het echt ver is.
  function coordInBins(bins, measure) {
    const n = bins.length;
    const avgWidth = Math.max((bins[n - 1].max - bins[0].min) / n, 0.1);
    if (measure < bins[0].min) {
      const d = (bins[0].min - measure) / avgWidth;
      return { c: Math.max(-0.75, -d), out: d > 0.75 };
    }
    if (measure > bins[n - 1].max) {
      const d = (measure - bins[n - 1].max) / avgWidth;
      return { c: Math.min(n + 0.75, n + d), out: d > 0.75 };
    }
    const k = bins.findIndex((b) => measure >= b.min && measure <= b.max);
    const width = Math.max(bins[k].max - bins[k].min, 0.1);
    return { c: k + (measure - bins[k].min) / width, out: false };
  }

  // Toon de knoptekst van het product zelf zodra die matcht — nooit een rauwe
  // chartcel als "46/48" terwijl de knop "48" heet. Elke knoptekst gaat naar
  // maximaal één rij (overlappende merkmappings als "46/48" en "48/50" zouden
  // anders allebei "48" claimen).
  function applyOptionLabels(sizes, optionValues) {
    const match = window.themeSizeMatch;
    const claimed = new Set();
    sizes.forEach((size) => {
      const optionValue = (optionValues || []).find((v) => !claimed.has(v) && match.matches(match.aliases(v), size.aliases));
      if (optionValue) {
        size.label = String(optionValue).trim();
        claimed.add(optionValue);
      }
    });
  }

  function computeAdvice(table, measures, fit, optionValues, fitBias) {
    if (!table) return null;
    const match = window.themeSizeMatch;
    const labelCols = table.columns.filter((c) => c.type === 'label');
    if (!labelCols.length) return null;

    const cols = table.columns.filter((c) => c.body && WEIGHTS[c.body] && measures[c.body] != null);
    let canonicalRows = null;
    let weightSum = 0;
    let coordSum = 0;
    let anyOut = false;

    cols.forEach((col) => {
      const bins = buildBins(table.rows, col.key);
      if (!bins) return;
      const rowsKey = bins.map((b) => b.row).join(',');
      if (canonicalRows === null) canonicalRows = { key: rowsKey, bins };
      else if (canonicalRows.key !== rowsKey) return; // kolom loopt niet gelijk met de rest
      const { c, out } = coordInBins(bins, measures[col.body]);
      coordSum += c * WEIGHTS[col.body];
      weightSum += WEIGHTS[col.body];
      if (out) anyOut = true;
    });

    if (!weightSum || !canonicalRows) return null;

    const binRows = canonicalRows.bins.map((b) => table.rows[b.row]);
    const cellText = (row, col) => String(row[col.key] == null ? '' : row[col.key]).trim();

    // Weergavekolom: de label-kolom die matcht met de echte variantwaarden van
    // het product wint (Barbour dames: UK "10" bij varianten "10/36"); anders
    // de eerste kolom waarin elke rij een unieke waarde heeft (Chevalier dames
    // heeft 2x "XS" in de alfa-kolom, de EU-kolom is wel uniek).
    const optionAliases = (optionValues || []).flatMap((v) => match.aliases(v));
    let displayCol = null;
    if (optionAliases.length) {
      let bestHits = 0;
      labelCols.forEach((col) => {
        const hits = binRows.filter((row) => {
          const value = cellText(row, col);
          return value && match.matches(match.aliases(value), optionAliases);
        }).length;
        if (hits > bestHits) { bestHits = hits; displayCol = col; }
      });
    }
    if (!displayCol) {
      displayCol = labelCols.find((col) => {
        const values = binRows.map((row) => cellText(row, col)).filter(Boolean);
        return values.length === binRows.length && new Set(values).size === values.length;
      }) || labelCols[0];
    }

    const sizes = binRows.map((row) => ({
      label: cellText(row, displayCol) || labelCols.map((col) => cellText(row, col)).find(Boolean) || '',
      aliases: labelCols.flatMap((col) => match.aliases(row[col.key]))
    }));
    if (sizes.some((s) => !s.label || !s.aliases.length)) return null; // labels moeten 1-op-1 met de bakken lopen

    if (optionAliases.length) applyOptionLabels(sizes, optionValues);
    const n = sizes.length;

    // fit_bias uit het chart-metaobject: positief = merk valt klein → groter advies
    const s = coordSum / weightSum + (fitBias || 0);
    let base = Math.floor(s);
    let frac = s - base;
    if (base < 0) { base = 0; frac = 0; }
    if (base > n - 1) { base = n - 1; frac = 1; }

    let index = base;
    if (fit === 'loose' && frac >= 0.5) index = Math.min(base + 1, n - 1);
    else if (fit !== 'slim' && frac >= 0.85) index = Math.min(base + 1, n - 1);

    // Fit-pill per maat: afstand van de gebruiker tot het midden van elke bak
    const deltas = sizes.map((size, k) => s - (k + 0.5));

    return {
      sizes,
      index,
      between: n > 1 && frac >= 0.75 && fit !== 'loose',
      out: anyOut,
      deltas
    };
  }

  const fitState = (delta) => {
    if (delta > 0.6) return 'tight';
    if (delta < -0.6) return 'loose';
    return 'good';
  };

  // Beste tabel voor dit product: de tabel waarvan de labels matchen met de
  // variantwaarden wint (Fjällräven: broek in "48" → de 42-60-tabel, jas →
  // de alfa-tabel). Fallback: eerste tabel met body-kolommen.
  function selectFinderTable(tables, optionValues, garmentHint) {
    const match = window.themeSizeMatch;
    let bodyTables = tables.filter((t) => t.columns.some((c) => c.body));
    if (!bodyTables.length) return null;
    // Broek-producten: rekenen op de taille/heup-tabel, nooit op de borsttabel
    if (garmentHint === 'bottoms') {
      const bottomTables = bodyTables.filter((t) => !t.columns.some((c) => c.body === 'chest')
        && t.columns.some((c) => c.body === 'waist' || c.body === 'hip'));
      if (bottomTables.length) bodyTables = bottomTables;
    }
    const optionAliases = (optionValues || []).flatMap((v) => match.aliases(v));
    if (optionAliases.length) {
      let best = null;
      let bestHits = 0;
      bodyTables.forEach((t) => {
        const labelCols = t.columns.filter((c) => c.type === 'label');
        const hits = t.rows.filter((row) => labelCols.some((col) => match.matches(match.aliases(row[col.key]), optionAliases))).length;
        if (hits > bestHits) { bestHits = hits; best = t; }
      });
      if (best) return best;
    }
    return bodyTables[0];
  }

  // Lengte telt niet mee in tabellen met alleen omtrekken, terwijl een lange
  // klant wél langere mouwen/rug nodig heeft (1,94 m met borst "L" draagt XL).
  // Kleine duw in maatstappen rond de NL-gemiddelde lengte; niet bij tabellen
  // die lengte zelf al meten (body:height, bv. Jagdhund) of broekentabellen.
  function heightBias(table, gender, height) {
    if (!table || !height) return 0;
    const hasChest = table.columns.some((c) => c.body === 'chest');
    const hasHeight = table.columns.some((c) => c.body === 'height');
    if (!hasChest || hasHeight) return 0;
    const ref = gender === 'male' ? 181 : gender === 'female' ? 167 : 174;
    return clamp((height - ref) / 40, -0.4, 0.4);
  }

  // Merk-eigen conversietabellen (tabellen zónder body-kolommen, met ≥2 label-
  // kolommen, bv. Barbour "L | 52-54") als alias-brug — alleen als de labels
  // van de adviestabel niet direct met de variantwaarden matchen.
  function expandViaCrosswalk(advice, tables, optionValues) {
    const match = window.themeSizeMatch;
    const optionAliases = (optionValues || []).flatMap((v) => match.aliases(v));
    if (!optionAliases.length) return;
    if (advice.sizes.some((s) => match.matches(s.aliases, optionAliases))) return;
    const bridges = tables.filter((t) => !t.columns.some((c) => c.body));
    if (!bridges.length) return;
    advice.sizes.forEach((size) => {
      // Alleen brug-rijen die de ORIGINELE aliassen raken — nooit doorketteren
      // via al toegevoegde aliassen (Härkila S=46/48 en M=48/50 delen "48";
      // ketteren zou 46 t/m 50 tot één kluwen smelten en labels laten schuiven).
      const original = size.aliases;
      const expanded = new Set(original);
      bridges.forEach((t) => {
        const labelCols = t.columns.filter((c) => c.type === 'label');
        if (labelCols.length < 2) return;
        t.rows.forEach((row) => {
          const rowAliases = labelCols.flatMap((col) => match.aliases(row[col.key]));
          if (rowAliases.some((a) => original.includes(a))) rowAliases.forEach((a) => expanded.add(a));
        });
      });
      size.aliases = Array.from(expanded);
    });
    // Bewust GEEN label-override na de brug: gebrugde labels zouden het
    // maatsysteem van de rij-weergave mixen (M naast 48). De rij houdt het
    // chart-systeem; ring/selecteren werken via de verbrede aliassen.
  }

  // Advies-events zonder lichaamsdata: alleen chart, geadviseerde en gekozen
  // maat gaan naar de dataLayer (GA4) om advies-accuratesse te kunnen meten.
  const pushDataLayer = (data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  /* ---- De pop (parametrisch silhouet) ---- */

  const FIGURE_Y = { neck: 46, shoulder: 66, chest: 104, waist: 152, hip: 198, bottom: 248 };

  function figurePath(gender, girths, height) {
    // Halve breedte per zone uit de omtrek; korter lichaam + zelfde omtrek → breder.
    // Afwijkingen t.o.v. een referentielichaam worden versterkt (×1.6) zodat
    // dikker/dunner duidelijk zichtbaar is in het silhouet.
    const REF = { chest: 96, waist: 82, hip: 98 };
    const hw = (girth, ref) => {
      const amplified = ref + ((girth || ref) - ref) * 1.6;
      return clamp(amplified * 0.29 * Math.sqrt(170 / Math.max(height || 175, 120)), 12, 68);
    };
    const wc = hw(girths.chest, REF.chest);
    const ww = hw(girths.waist, REF.waist);
    const wh = hw(girths.hip, REF.hip);
    const shoulderF = gender === 'male' ? 1.22 : gender === 'female' ? 1.04 : 1.13;
    const sw = clamp(wc * shoulderF + 3, wc, 72);
    const nw = 9;
    const cx = 100;
    const y = FIGURE_Y;
    const r = (v) => Math.round(v * 10) / 10;
    const d = [
      `M ${cx - nw} ${y.neck}`,
      `C ${cx - nw} ${y.neck + 10}, ${cx - r(sw * 0.7)} ${y.shoulder - 8}, ${cx - r(sw)} ${y.shoulder}`,
      `C ${cx - r(sw) - 2} ${y.shoulder + 12}, ${cx - r(wc)} ${y.chest - 12}, ${cx - r(wc)} ${y.chest}`,
      `C ${cx - r(wc)} ${y.chest + 18}, ${cx - r(ww)} ${y.waist - 14}, ${cx - r(ww)} ${y.waist}`,
      `C ${cx - r(ww)} ${y.waist + 14}, ${cx - r(wh)} ${y.hip - 16}, ${cx - r(wh)} ${y.hip}`,
      `C ${cx - r(wh)} ${y.hip + 24}, ${cx - r(wh * 0.8)} ${y.bottom - 8}, ${cx - r(wh * 0.45)} ${y.bottom}`,
      `L ${cx + r(wh * 0.45)} ${y.bottom}`,
      `C ${cx + r(wh * 0.8)} ${y.bottom - 8}, ${cx + r(wh)} ${y.hip + 24}, ${cx + r(wh)} ${y.hip}`,
      `C ${cx + r(wh)} ${y.hip - 16}, ${cx + r(ww)} ${y.waist + 14}, ${cx + r(ww)} ${y.waist}`,
      `C ${cx + r(ww)} ${y.waist - 14}, ${cx + r(wc)} ${y.chest + 18}, ${cx + r(wc)} ${y.chest}`,
      `C ${cx + r(wc)} ${y.chest - 12}, ${cx + r(sw) + 2} ${y.shoulder + 12}, ${cx + r(sw)} ${y.shoulder}`,
      `C ${cx + r(sw * 0.7)} ${y.shoulder - 8}, ${cx + nw} ${y.neck + 10}, ${cx + nw} ${y.neck}`,
      'Z'
    ].join(' ');
    return { d, widths: { chest: r(wc), waist: r(ww), hip: r(wh) } };
  }

  /* ---- Component ---- */

  class SizeFinder extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.genderHint = this.dataset.genderHint || '';
      this.garmentHint = this.dataset.garmentHint || '';
      this.fitBias = clamp(parseFloat(String(this.dataset.fitBias || '').replace(',', '.')) || 0, -1, 1);

      try {
        const tables = JSON.parse(this.querySelector('[data-sf-tables]').textContent);
        this.tables = tables.filter((t) => t && t.columns && t.rows);
        this.table = this.tables.find((t) => t.columns.some((c) => c.body)) || null;
        this.strings = JSON.parse(this.querySelector('[data-sf-strings]').textContent);
      } catch (e) {
        this.table = null;
      }
      if (!this.table) {
        this.hidden = true;
        return;
      }
      this.chartHandle = this.dataset.chartHandle || '';

      this.dialogEl = this.closest('dialog');
      this.form = this.querySelector('[data-sf-view="form"]');
      this.views = {
        promo: this.querySelector('[data-sf-view="promo"]'),
        form: this.form,
        result: this.querySelector('[data-sf-view="result"]')
      };
      this.steps = Array.from(this.querySelectorAll('[data-sf-step]'));
      this.currentStep = 0;
      this.previewIndex = null;
      this.advice = null;

      this.addEventListener('click', this.handleClick.bind(this));
      this.form.addEventListener('submit', (evt) => {
        evt.preventDefault();
        this.next();
      });
      this.form.elements.gender.forEach((input) => {
        input.addEventListener('change', () => { this.girthsDirty = false; });
      });
      ['chest', 'waist', 'hip', 'inseam'].forEach((key) => {
        const input = this.form.elements[key];
        if (input) {
          input.addEventListener('input', () => {
            this.girthsDirty = true;
            this.renderFigure();
          });
        }
      });

      const sizeRow = this.querySelector('[data-sf-sizes]');
      if (sizeRow) sizeRow.addEventListener('keydown', this.handleSizeRowKeys.bind(this));

      this.girthsDirty = false;
      this.initFigure();
      this.initNotify();
      this.hydrate();

      // Advies vs. gekochte maat meten bij add-to-cart (geen lichaamsdata)
      document.addEventListener('submit', (evt) => {
        const form = evt.target;
        if (!this.advice || !form.action || form.action.indexOf('/cart/add') === -1) return;
        const picker = this.getPicker();
        const section = form.closest('.shopify-section');
        if (section && picker && !section.contains(picker)) return;
        const checked = picker ? picker.querySelector('.js-option:checked') : null;
        const advised = this.advice.sizes[this.advice.index];
        pushDataLayer({
          event: 'size_finder_atc',
          sizeChart: this.chartHandle,
          sizeAdvised: advised.label,
          sizeChosen: checked ? checked.value : null,
          followedAdvice: checked
            ? window.themeSizeMatch.matches(window.themeSizeMatch.aliases(checked.value), advised.aliases)
            : null
        });
      }, true);
    }

    /* ---- De pop ---- */

    initFigure() {
      this.figureEl = this.querySelector('[data-sf-figure]');
      if (!this.figureEl) return;
      this.figureBody = this.figureEl.querySelector('[data-sf-figure-body]');
      this.sliderBase = null;

      this.form.querySelectorAll('input[name="mode"]').forEach((input) => {
        input.addEventListener('change', () => this.setMode(input.value));
      });
      this.querySelectorAll('[data-sf-slider]').forEach((slider) => {
        slider.addEventListener('input', () => this.applySliders());
      });
      // Meetlijn oplichten bij focus op het bijbehorende veld of de slider
      this.form.addEventListener('focusin', (evt) => {
        const name = String(evt.target.name || '').replace(/^adj-/, '');
        this.figureEl.querySelectorAll('[data-sf-line]').forEach((line) => {
          line.classList.toggle('is-focus', line.dataset.sfLine === name);
        });
      });
    }

    setMode(mode) {
      this.querySelectorAll('[data-sf-mode-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.sfModePanel !== mode;
      });
      // Terug naar silhouet: sliders opnieuw ijken op de (eventueel aangepaste) waarden
      if (mode === 'silhouette') this.rebaseline();
    }

    currentGirths() {
      const about = this.readAbout();
      const est = this.lastEstimate || estimateBody(about.gender, about.age, about.height, about.weight);
      const girths = {};
      GIRTHS.forEach((key) => {
        const input = this.form.elements[key];
        const value = input ? parseFloat(String(input.value).replace(',', '.')) : NaN;
        girths[key] = Number.isNaN(value) ? est[key] : value;
      });
      return girths;
    }

    renderFigure() {
      if (!this.figureBody) return;
      const about = this.readAbout();
      const figure = figurePath(about.gender, this.currentGirths(), about.height);
      this.figureBody.setAttribute('d', figure.d);
      if (window.CSS && CSS.supports && CSS.supports('d', 'path("M 0 0")')) {
        this.figureBody.style.d = `path("${figure.d}")`;
      }
      this.figureEl.querySelectorAll('[data-sf-line]').forEach((line) => {
        const width = figure.widths[line.dataset.sfLine];
        if (width) line.setAttribute('rx', width + 5);
      });
    }

    rebaseline() {
      this.sliderBase = this.currentGirths();
      this.querySelectorAll('[data-sf-slider]').forEach((slider) => { slider.value = 0; });
    }

    applySliders() {
      if (!this.sliderBase) this.rebaseline();
      const overallEl = this.form.elements['adj-overall'];
      const overall = overallEl ? parseFloat(overallEl.value) || 0 : 0;
      GIRTHS.forEach((key) => {
        const input = this.form.elements[key];
        if (!input) return;
        const adjEl = this.form.elements[`adj-${key}`];
        const adj = adjEl ? parseFloat(adjEl.value) || 0 : 0;
        input.value = Math.round((this.sliderBase[key] + overall + adj) * 2) / 2;
      });
      this.girthsDirty = true;
      this.renderFigure();
    }

    resetSilhouette() {
      const about = this.readAbout();
      const est = estimateBody(about.gender, about.age, about.height, about.weight);
      this.lastEstimate = est;
      GIRTHS.concat(['inseam']).forEach((key) => {
        const input = this.form.elements[key];
        if (input) input.value = est[key];
      });
      this.girthsDirty = false;
      this.rebaseline();
      this.renderFigure();
    }

    /* ---- Profiel ---- */

    loadStore() {
      try { return JSON.parse(storage.get(STORE_KEY)) || {}; } catch (e) { return {}; }
    }

    // Profielkeuze: het profiel dat past bij het geslacht van de chart wint
    // (gedeeld device: man vult heren in, vrouw opent dames → zij krijgt háár
    // profiel), daarna het laatst gebruikte, daarna wat er maar is.
    profile() {
      const store = this.loadStore();
      if (!store.profiles) return null;
      const order = [this.genderHint, store.gender, 'male', 'female', 'other'].filter(Boolean);
      for (const gender of order) {
        const p = store.profiles[gender];
        if (p && p.age && p.height && p.weight) return Object.assign({ gender }, p);
      }
      return null;
    }

    saveProfile(data) {
      const store = this.loadStore();
      store.v = 1;
      store.gender = data.gender;
      store.profiles = store.profiles || {};
      store.profiles[data.gender] = {
        age: data.age,
        height: data.height,
        weight: data.weight,
        fit: data.fit,
        edited: data.edited,
        chest: data.chest,
        waist: data.waist,
        hip: data.hip,
        inseam: data.inseam,
        updatedAt: Date.now()
      };
      storage.set(STORE_KEY, JSON.stringify(store));
    }

    // Maten voor het advies: bij handmatig aangepaste maten winnen die,
    // anders wordt er per chart opnieuw geschat uit leeftijd/lengte/gewicht.
    measuresFor(profile) {
      const est = estimateBody(profile.gender, profile.age, profile.height, profile.weight);
      if (!profile.edited) return est;
      const merged = Object.assign({}, est);
      GIRTHS.concat(['inseam']).forEach((key) => {
        if (profile[key] != null) merged[key] = profile[key];
      });
      return merged;
    }

    /* ---- Hydrate (page load / terugkerende bezoeker) ---- */

    hydrate() {
      const profile = this.profile();
      if (!profile) return;
      this.prefill(profile);
      // Ander geslacht dan de chart (bv. heren-profiel op een dames-chart):
      // geen automatisch advies — wel de wizard klaargezet met het geslacht
      // van de chart voorgeselecteerd. 'Anders'-profielen tellen als neutraal.
      if (this.genderHint && profile.gender !== this.genderHint && profile.gender !== 'other') {
        if (this.form.elements.gender) this.form.elements.gender.value = this.genderHint;
        return;
      }
      const optionValues = this.getOptionValues();
      const table = selectFinderTable(this.tables, optionValues, this.garmentHint) || this.table;
      const bias = this.fitBias + heightBias(table, profile.gender, profile.height);
      const advice = computeAdvice(table, this.measuresFor(profile), profile.fit || 'regular', optionValues, bias);
      if (!advice) return;
      expandViaCrosswalk(advice, this.tables, optionValues);
      this.advice = advice;
      pushDataLayer({
        event: 'size_finder_advice_recall',
        sizeChart: this.chartHandle,
        sizeAdvised: advice.sizes[advice.index].label
      });
      this.renderPromoAdvice(profile, advice);
      this.applyTriggerBadge(this.advisedLabel());
      this.markAdvisedOption();
      this.autoSelectAdvised();
      this.observeTriggerSwaps();
    }

    prefill(profile) {
      const el = this.form.elements;
      el.gender.value = profile.gender;
      el.age.value = profile.age;
      el.height.value = profile.height;
      el.weight.value = profile.weight;
      if (el.fit && profile.fit) el.fit.value = profile.fit;
      if (profile.edited) {
        GIRTHS.concat(['inseam']).forEach((key) => {
          if (el[key] && profile[key] != null) el[key].value = profile[key];
        });
        this.girthsDirty = true;
      }
    }

    renderPromoAdvice(profile, advice) {
      const size = advice.sizes[advice.index];
      this.views.promo.querySelector('[data-sf-promo-title]').textContent = this.strings.yourSize.replace('[[size]]', size.label);
      const sub = this.views.promo.querySelector('[data-sf-promo-sub]');
      const selectBtn = this.views.promo.querySelector('[data-sf-promo-select]');
      const notifyBtn = this.views.promo.querySelector('[data-sf-notify-open]');
      const available = this.findOption(size);
      // Uitverkocht = het product verkoopt deze maat wél, maar nu zonder voorraad
      const soldOut = !available && !!this.findAnyOption(size);
      this.promoTarget = null;

      if (soldOut) {
        const alt = this.bestAlternative(advice);
        let text = this.strings.soldOut.replace('[[size]]', size.label);
        if (alt) text += ' ' + this.fitSentence(alt);
        sub.textContent = text;
        selectBtn.hidden = !alt;
        if (alt) {
          selectBtn.textContent = this.strings.selectSize.replace('[[size]]', alt.label);
          this.promoTarget = alt;
        }
        if (notifyBtn) {
          notifyBtn.hidden = false;
          notifyBtn.dataset.sfNotifySize = size.label;
        }
      } else {
        sub.textContent = profile.edited ? this.strings.basedOnMeasured : this.strings.basedOnEstimate;
        selectBtn.hidden = !available;
        // Knoptekst = de variantwaarde die daadwerkelijk geselecteerd wordt
        if (available) {
          selectBtn.textContent = this.strings.selectSize.replace('[[size]]', String(available.value).trim());
          this.promoTarget = size;
        }
        if (notifyBtn) notifyBtn.hidden = true;
      }
      this.querySelector('[data-sf-start]').textContent = this.strings.adjust;
      this.highlightTableRow(size);
    }

    // "Maat 52 is wel leverbaar (valt iets ruimer)."
    fitSentence(entry) {
      const fitText = Math.abs(entry.delta) <= 0.6
        ? this.strings.fitGood.toLowerCase()
        : entry.delta < 0 ? this.strings.slightlyLoose : this.strings.slightlyTight;
      return this.strings.alternativeFits
        .replace('[[size]]', entry.label)
        .replace('[[fit]]', fitText);
    }

    /* ---- Views & wizard ---- */

    showView(name) {
      Object.keys(this.views).forEach((key) => { this.views[key].hidden = key !== name; });
      if (this.notifyEl && !this.notifyPosted) this.notifyEl.hidden = true;
      if (this.dialogEl) this.dialogEl.classList.toggle('size-chart--finder-active', name !== 'promo');
    }

    showStep(index) {
      this.currentStep = index;
      this.steps.forEach((step) => { step.hidden = parseInt(step.dataset.sfStep, 10) !== index; });
      const backBtn = this.querySelector('[data-sf-back]');
      const nextBtn = this.querySelector('[data-sf-next]');
      backBtn.hidden = index === 0;
      nextBtn.textContent = index === this.steps.length - 1 ? nextBtn.dataset.finishLabel : nextBtn.dataset.nextLabel;
      const progress = this.querySelector('[data-sf-progress]');
      progress.textContent = this.strings.progress
        .replace('[[current]]', index + 1)
        .replace('[[total]]', this.steps.length);
      this.querySelector('[data-sf-progress-fill]').style.width = `${((index + 1) / this.steps.length) * 100}%`;
      this.showView('form');
    }

    readAbout() {
      const el = this.form.elements;
      return {
        gender: el.gender.value || 'other',
        age: clamp(parseFloat(el.age.value) || 35, 16, 99),
        height: clamp(parseFloat(el.height.value) || 175, 120, 220),
        weight: clamp(parseFloat(el.weight.value) || 78, 35, 200)
      };
    }

    next() {
      if (this.currentStep === 0) {
        const about = this.readAbout();
        const est = estimateBody(about.gender, about.age, about.height, about.weight);
        this.lastEstimate = est;
        GIRTHS.concat(['inseam']).forEach((key) => {
          const input = this.form.elements[key];
          if (input && (!this.girthsDirty || !input.value)) input.value = est[key];
        });
        pushDataLayer({ event: 'size_finder_step_complete', sizeChart: this.chartHandle, step: 'about' });
        this.showStep(1);
        this.rebaseline();
        this.renderFigure();
      } else {
        this.finish();
      }
    }

    finish() {
      const about = this.readAbout();
      const est = estimateBody(about.gender, about.age, about.height, about.weight);
      const el = this.form.elements;
      const data = Object.assign({ fit: el.fit ? el.fit.value : 'regular', edited: false }, about);

      GIRTHS.concat(['inseam']).forEach((key) => {
        if (!el[key]) return;
        const value = parseFloat(String(el[key].value).replace(',', '.'));
        if (!Number.isNaN(value)) {
          data[key] = value;
          if (Math.abs(value - est[key]) > 1) data.edited = true;
        }
      });

      this.saveProfile(data);
      const optionValues = this.getOptionValues();
      const table = selectFinderTable(this.tables, optionValues, this.garmentHint) || this.table;
      const bias = this.fitBias + heightBias(table, data.gender, data.height);
      const advice = computeAdvice(table, this.measuresFor(data), data.fit, optionValues, bias);
      this.advice = advice;
      if (!advice) {
        this.renderNoAdvice();
        return;
      }
      expandViaCrosswalk(advice, this.tables, optionValues);
      pushDataLayer({
        event: 'size_finder_advice',
        sizeChart: this.chartHandle,
        sizeAdvised: advice.sizes[advice.index].label,
        basis: data.edited ? 'measured' : 'estimated',
        outOfRange: advice.out
      });
      this.renderResult(data, advice);
      this.renderPromoAdvice(data, advice);
      this.applyTriggerBadge(this.advisedLabel());
      this.markAdvisedOption();
      this.observeTriggerSwaps();
    }

    /* ---- Resultaat ---- */

    renderNoAdvice() {
      pushDataLayer({ event: 'size_finder_no_advice', sizeChart: this.chartHandle });
      this.views.result.querySelector('[data-sf-result-size]').textContent = '—';
      this.views.result.querySelector('[data-sf-sizes]').innerHTML = '';
      this.views.result.querySelector('[data-sf-fitline]').textContent = this.strings.noAdvice;
      this.views.result.querySelector('[data-sf-note]').hidden = true;
      this.views.result.querySelector('[data-sf-select]').hidden = true;
      this.showView('result');
    }

    // De maatrij toont de knopjes van het PRODUCT zelf (in picker-volgorde),
    // elk met de fit van de best passende chart-rij — zo klopt de rij 1-op-1
    // met wat de klant kan kopen. Zonder picker: terugval op de chart-rijen.
    buildDisplaySizes(advice) {
      const match = window.themeSizeMatch;
      const inputs = this.getSizeInputs();
      if (inputs.length) {
        const list = inputs.map((input) => {
          const aliases = match.aliases(input.value);
          let best = null;
          advice.sizes.forEach((size, i) => {
            if (!match.matches(aliases, size.aliases)) return;
            if (best === null || Math.abs(advice.deltas[i]) < Math.abs(advice.deltas[best])) best = i;
          });
          return {
            label: String(input.value).trim(),
            aliases,
            delta: best === null ? null : advice.deltas[best],
            advised: best !== null && best === advice.index,
            soldOut: input.classList.contains('is-unavailable')
          };
        });
        if (list.some((entry) => entry.delta !== null)) {
          let index = list.findIndex((entry) => entry.advised);
          if (index < 0) {
            // Geadviseerde chartmaat wordt niet verkocht: dichtstbijzijnde
            // knop alleen aanwijzen als die ook echt past.
            list.forEach((entry, i) => {
              if (entry.delta !== null && (index < 0 || Math.abs(entry.delta) < Math.abs(list[index].delta))) index = i;
            });
            if (index >= 0 && Math.abs(list[index].delta) > 0.6) index = -1;
          }
          return { list, index };
        }
      }
      return {
        list: advice.sizes.map((size, i) => ({
          label: size.label,
          aliases: size.aliases,
          delta: advice.deltas[i],
          advised: i === advice.index,
          soldOut: false
        })),
        index: advice.index
      };
    }

    renderResult(profile, advice) {
      const display = this.buildDisplaySizes(advice);
      this.display = display;
      this.previewIndex = display.index >= 0 ? display.index : null;
      this.views.result.querySelector('[data-sf-result-size]').textContent = display.index >= 0
        ? display.list[display.index].label
        : advice.sizes[advice.index].label;

      const row = this.views.result.querySelector('[data-sf-sizes]');
      row.innerHTML = '';
      display.list.forEach((entry, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = entry.delta === null
          ? 'size-finder__size size-finder__size--unknown'
          : `size-finder__size size-finder__size--${fitState(entry.delta)}`;
        if (entry.soldOut) btn.classList.add('size-finder__size--soldout');
        btn.setAttribute('role', 'option');
        if (entry.delta !== null) btn.dataset.sfSizeIndex = i;
        else btn.disabled = true;
        btn.appendChild(Object.assign(document.createElement('span'), {
          className: 'size-finder__size-label',
          textContent: entry.label
        }));
        if (i === display.index) {
          btn.classList.add('is-recommended');
          btn.title = this.strings.recommended;
        }
        row.appendChild(btn);
      });

      const note = this.views.result.querySelector('[data-sf-note]');
      if (advice.out) {
        note.textContent = this.strings.outOfRange.replace('[[size]]', advice.sizes[advice.index].label);
        note.hidden = false;
      } else if (advice.between) {
        note.textContent = this.strings.betweenSizes;
        note.hidden = false;
      } else {
        note.textContent = profile.edited ? this.strings.basedOnMeasured : this.strings.basedOnEstimate;
        note.hidden = false;
      }

      if (this.previewIndex !== null) {
        this.previewSize(this.previewIndex);
      } else {
        this.views.result.querySelector('[data-sf-fitline]').textContent = '';
        this.views.result.querySelector('[data-sf-select]').hidden = true;
      }
      this.showView('result');
    }

    previewSize(index) {
      const entry = this.display && this.display.list[index];
      if (!entry || entry.delta === null) return;
      this.previewIndex = index;
      const state = fitState(entry.delta);
      const stateText = state === 'tight' ? this.strings.fitTight
        : state === 'loose' ? this.strings.fitLoosePill : this.strings.fitGood;

      this.views.result.querySelectorAll('[data-sf-size-index]').forEach((btn) => {
        const active = parseInt(btn.dataset.sfSizeIndex, 10) === index;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      const fitline = this.views.result.querySelector('[data-sf-fitline]');
      const selectBtn = this.views.result.querySelector('[data-sf-select]');
      const notifyBtn = this.views.result.querySelector('[data-sf-notify-open]');

      if (entry.soldOut) {
        fitline.textContent = this.strings.soldOut.replace('[[size]]', entry.label);
        selectBtn.hidden = true;
        if (notifyBtn) {
          notifyBtn.hidden = false;
          notifyBtn.dataset.sfNotifySize = entry.label;
        }
        return;
      }

      fitline.textContent = this.strings.fitLine
        .replace('[[size]]', entry.label)
        .replace('[[fit]]', stateText.toLowerCase());
      if (notifyBtn) notifyBtn.hidden = true;
      const option = this.findOption(entry);
      selectBtn.hidden = !option;
      if (option) selectBtn.textContent = this.strings.selectSize.replace('[[size]]', String(option.value).trim());
    }

    handleSizeRowKeys(evt) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(evt.key)) return;
      const buttons = Array.from(this.views.result.querySelectorAll('[data-sf-size-index]'));
      if (!buttons.length) return;
      const current = buttons.findIndex((btn) => btn === document.activeElement || btn.classList.contains('is-active'));
      let target;
      if (evt.key === 'Home') target = 0;
      else if (evt.key === 'End') target = buttons.length - 1;
      else {
        const dir = evt.key === 'ArrowRight' ? 1 : -1;
        target = clamp((current < 0 ? 0 : current) + dir, 0, buttons.length - 1);
      }
      evt.preventDefault();
      buttons[target].focus();
      this.previewSize(parseInt(buttons[target].dataset.sfSizeIndex, 10));
    }

    // Badge-tekst: de knoptekst van het product als die er is, anders het chart-label
    advisedLabel() {
      const size = this.advice.sizes[this.advice.index];
      const option = this.findOption(size);
      return option ? String(option.value).trim() : size.label;
    }

    /* ---- Variant-koppeling & trigger-badge ---- */

    getPicker() {
      return document.querySelector(`variant-picker[data-section-id="${this.sectionId}"]`);
    }

    // Alleen de inputs van de MAAT-optie (niet kleur): scope op de
    // option-selector met "maat"/"size" in de naam, anders de hele picker.
    getSizeInputs() {
      const picker = this.getPicker();
      if (!picker) return [];
      const selectors = Array.from(picker.querySelectorAll('.option-selector'));
      const sizeSelector = selectors.find((el) => /maat|size|gr(ö|o)sse/i.test(el.dataset.option || ''));
      return Array.from((sizeSelector || picker).querySelectorAll('.js-option'));
    }

    getOptionValues() {
      return this.getSizeInputs().map((input) => input.value);
    }

    findOption(size) {
      const match = window.themeSizeMatch;
      const wanted = size.aliases && size.aliases.length ? size.aliases : match.aliases(size.label || size);
      return this.getSizeInputs()
        .find((input) => !input.classList.contains('is-unavailable') && match.matches(match.aliases(input.value), wanted)) || null;
    }

    // Ook uitverkochte knoppen tellen mee: onderscheidt "uitverkocht" van "niet verkocht"
    findAnyOption(size) {
      const match = window.themeSizeMatch;
      const wanted = size.aliases && size.aliases.length ? size.aliases : match.aliases(size.label || size);
      return this.getSizeInputs()
        .find((input) => match.matches(match.aliases(input.value), wanted)) || null;
    }

    // Best passende maat die nog wél op voorraad is. Drempel 0.9: buurmaten
    // liggen ~1 maatstap van het ideaal, dus 0.6 (de fit-pill-grens) zou
    // vrijwel nooit een alternatief opleveren; tot 0.9 melden we eerlijk
    // "valt iets ruimer/krapper", daarboven alleen nog de notify-CTA.
    bestAlternative(advice) {
      const match = window.themeSizeMatch;
      let best = null;
      this.getSizeInputs().forEach((input) => {
        if (input.classList.contains('is-unavailable')) return;
        const aliases = match.aliases(input.value);
        let delta = null;
        advice.sizes.forEach((size, i) => {
          if (!match.matches(aliases, size.aliases)) return;
          if (delta === null || Math.abs(advice.deltas[i]) < Math.abs(delta)) delta = advice.deltas[i];
        });
        if (delta === null || Math.abs(delta) > 0.9) return;
        if (!best || Math.abs(delta) < Math.abs(best.delta)) {
          best = { label: String(input.value).trim(), aliases, delta };
        }
      });
      return best;
    }

    selectSize(size) {
      const option = this.findOption(size);
      if (!option) return;
      const match = window.themeSizeMatch;
      const advised = this.advice ? this.advice.sizes[this.advice.index] : null;
      pushDataLayer({
        event: 'size_finder_select',
        sizeChart: this.chartHandle,
        sizeSelected: String(option.value).trim(),
        sizeAdvised: advised ? advised.label : null,
        followedAdvice: advised ? match.matches(match.aliases(option.value), advised.aliases) : null
      });
      if (!option.checked) {
        option.checked = true;
        option.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const component = this.closest('size-chart-dialog');
      if (component) component.close();
    }

    /* ---- Uitverkocht: e-mail achterlaten (Shopify-contactformulier) ---- */

    initNotify() {
      this.notifyEl = this.querySelector('[data-sf-notify]');
      if (!this.notifyEl) return;
      this.klaviyoCompany = this.dataset.klaviyoCompany || '';
      try {
        this.variantData = JSON.parse(this.querySelector('[data-sf-variants]').textContent);
      } catch (e) {
        this.variantData = [];
      }
      const form = this.notifyEl.querySelector('form');
      if (form) {
        form.addEventListener('submit', (evt) => {
          const email = form.querySelector('input[type="email"]');
          const variant = this.klaviyoCompany ? this.resolveNotifyVariant(this.notifyTarget || '') : null;
          pushDataLayer({
            event: 'size_finder_notify',
            sizeChart: this.chartHandle,
            sizeRequested: this.notifyTarget || '',
            channel: variant ? 'klaviyo' : 'contact_form'
          });
          // Klaviyo back-in-stock via de client-API (geen reload, geen captcha);
          // lukt dat niet, dan valt de submit terug op de native contact-POST.
          if (!variant || !email || !email.value) return;
          evt.preventDefault();
          this.submitKlaviyoBis(email.value, variant, form);
        });
      }
      // Na een geslaagde POST herlaadt de pagina: dialog heropenen met de succesmelding
      if (this.notifyEl.querySelector('[data-sf-notify-posted]')) {
        this.notifyPosted = true;
        this.notifyEl.hidden = false;
        setTimeout(() => {
          const component = this.closest('size-chart-dialog');
          if (component && component.open) component.open(null);
        }, 0);
      }
    }

    // Variant voor de back-in-stock-inschrijving: maat matcht het gevraagde label,
    // overige opties (bv. kleur) zo dicht mogelijk bij de huidige selectie, en
    // liefst een variant die daadwerkelijk uitverkocht is.
    resolveNotifyVariant(sizeLabel) {
      const match = window.themeSizeMatch;
      const wanted = match.aliases(sizeLabel);
      if (!wanted.length) return null;
      const candidates = (this.variantData || []).filter((v) => match.matches(match.aliases(v.size), wanted));
      if (!candidates.length) return null;
      const picker = this.getPicker();
      const selected = picker
        ? Array.from(picker.querySelectorAll('.js-option:checked')).map((input) => input.value)
        : [];
      const score = (v) => v.options.reduce((n, o) => n + (selected.includes(o) ? 1 : 0), 0) + (v.available ? 0 : 0.5);
      return candidates.sort((a, b) => score(b) - score(a))[0];
    }

    async submitKlaviyoBis(email, variant, form) {
      try {
        const res = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.klaviyoCompany)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email } } }
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant.id}` } }
              }
            }
          })
        });
        if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
        const fields = this.notifyEl.querySelector('[data-sf-notify-fields]');
        const done = this.notifyEl.querySelector('[data-sf-notify-done]');
        if (fields) fields.hidden = true;
        if (done) done.hidden = false;
      } catch (e) {
        form.submit(); // terugval: contactformulier naar de shop-inbox
      }
    }

    openNotify(sizeLabel) {
      if (!this.notifyEl) return;
      this.notifyTarget = sizeLabel;
      this.notifyEl.hidden = false;
      const title = this.notifyEl.querySelector('[data-sf-notify-title]');
      if (title) title.textContent = this.strings.notifyTitle.replace('[[size]]', sizeLabel);
      const sizeInput = this.notifyEl.querySelector('[data-sf-notify-size]');
      if (sizeInput) sizeInput.value = sizeLabel;
      const email = this.notifyEl.querySelector('input[type="email"]');
      if (email) email.focus();
    }

    highlightTableRow(size) {
      const match = window.themeSizeMatch;
      const wanted = size.aliases && size.aliases.length ? size.aliases : match.aliases(size.label || size);
      const scope = this.closest('size-chart-dialog') || document;
      scope.querySelectorAll('tbody tr[data-size-labels]').forEach((row) => {
        const rowAliases = row.dataset.sizeLabels.split('|').filter(Boolean).flatMap((l) => match.aliases(l));
        row.classList.toggle('is-advised', match.matches(rowAliases, wanted));
      });
    }

    applyTriggerBadge(sizeLabel) {
      const text = this.strings.yourSize.replace('[[size]]', sizeLabel);
      document.querySelectorAll(`[data-size-chart-open="size-chart-${this.sectionId}"]`).forEach((trigger) => {
        const label = trigger.querySelector('[data-sf-trigger-label]');
        if (!label || label.textContent === text) return; // al gezet: voorkomt observer-loop
        label.textContent = text;
        trigger.classList.add('size-chart-trigger--advice');
      });
    }

    // Ring om de geadviseerde maat in de variant picker (ook als uitverkocht)
    markAdvisedOption() {
      if (!this.advice) return;
      const match = window.themeSizeMatch;
      const picker = this.getPicker();
      if (!picker) return;
      const wanted = this.advice.sizes[this.advice.index].aliases;
      this.getSizeInputs().forEach((input) => {
        const label = picker.querySelector(`label[for="${input.id}"]`);
        if (!label) return;
        const advised = match.matches(match.aliases(input.value), wanted);
        if (label.classList.contains('is-size-advised') !== advised) {
          label.classList.toggle('is-size-advised', advised);
          if (advised) label.title = this.strings.recommended;
          else label.removeAttribute('title');
        }
      });
    }

    // Eenmalig per paginaload: geadviseerde maat voorselecteren, behalve bij
    // een expliciete variant-URL (gedeelde link) of een uitverkochte maat.
    autoSelectAdvised() {
      if (this.didAutoSelect || !this.advice) return;
      this.didAutoSelect = true;
      if (/[?&]variant=/.test(window.location.search)) return;
      const option = this.findOption(this.advice.sizes[this.advice.index]);
      if (!option || option.checked) return;
      option.checked = true;
      option.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Variant-wissels vervangen de trigger-DOM (section rendering); badge opnieuw zetten.
    observeTriggerSwaps() {
      if (this.triggerObserver) return;
      this.triggerObserver = new MutationObserver(() => {
        clearTimeout(this.badgeTimer);
        this.badgeTimer = setTimeout(() => {
          if (!this.advice) return;
          this.applyTriggerBadge(this.advisedLabel());
          this.markAdvisedOption();
        }, 60);
      });
      const scope = document.getElementById(`shopify-section-${this.sectionId}`) || document.body;
      this.triggerObserver.observe(scope, { childList: true, subtree: true });
    }

    /* ---- Events ---- */

    handleClick(evt) {
      const stepBtn = evt.target.closest('[data-sf-minus], [data-sf-plus]');
      if (stepBtn) {
        const input = stepBtn.closest('.size-finder__stepper').querySelector('input');
        const step = parseFloat(input.step) || 1;
        const delta = stepBtn.hasAttribute('data-sf-minus') ? -step : step;
        const value = (parseFloat(input.value) || 0) + delta;
        input.value = Math.round(clamp(value, parseFloat(input.min), parseFloat(input.max)) * 2) / 2;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }

      if (evt.target.closest('[data-sf-start]')) {
        pushDataLayer({ event: 'size_finder_start', sizeChart: this.chartHandle, resumed: !!this.advice });
        this.showStep(0);
        return;
      }
      if (evt.target.closest('[data-sf-back]')) {
        this.showStep(this.currentStep - 1);
        return;
      }
      if (evt.target.closest('[data-sf-restart]')) {
        pushDataLayer({ event: 'size_finder_start', sizeChart: this.chartHandle, resumed: true });
        this.showStep(0);
        return;
      }
      const notifyOpenBtn = evt.target.closest('[data-sf-notify-open]');
      if (notifyOpenBtn) {
        this.openNotify(notifyOpenBtn.dataset.sfNotifySize || '');
        return;
      }
      if (evt.target.closest('[data-sf-reset]')) {
        this.resetSilhouette();
        return;
      }
      if (evt.target.closest('[data-sf-clear]')) {
        this.clearData();
        return;
      }
      if (evt.target.closest('[data-sf-promo-select]')) {
        const target = this.promoTarget || (this.advice && this.advice.sizes[this.advice.index]);
        if (target) this.selectSize(target);
        return;
      }
      if (evt.target.closest('[data-sf-select]')) {
        if (this.display && this.previewIndex != null) this.selectSize(this.display.list[this.previewIndex]);
        return;
      }
      const sizeBtn = evt.target.closest('[data-sf-size-index]');
      if (sizeBtn) this.previewSize(parseInt(sizeBtn.dataset.sfSizeIndex, 10));
    }

    clearData() {
      storage.remove(STORE_KEY);
      // Herladen zet promo, trigger en formulier terug naar de schone begintoestand
      window.location.reload();
    }
  }

  customElements.define('size-finder', SizeFinder);

  // Pure rekenkern voor de regressietests (tests/size-finder.test.mjs) en console-debugging
  window.themeSizeFinderInternals = {
    estimateBody, buildBins, coordInBins, computeAdvice, selectFinderTable, expandViaCrosswalk, figurePath, heightBias
  };
}
